<template>

	<AppPopup prevent-overlay-dismiss :buttons="[]" :title="''" :max-width="1100" ref="popup">
		<div class="funnel-choice-wrapper" id="funnel-choice-wrapper">
			<AppPagination
				ref="pagination"
				:pages="pages"
				:page-index="pageIndex"
				:hide-header="true"
				:hide-dots="true"
				:no-navigation-buttons="true"
				:change-height="true">

				<!-- Page 1: the 3 choices -->
				<template #choices>
					<div class="choices-page">
						<div class="choices-header">
							<div class="header-heading">
								<h2 class="header-title">{{ getString('funnel_choice_title') }}</h2>
								<p class="header-subtitle">{{ getString('funnel_choice_subtitle') }}</p>
							</div>
							<p class="header-intro">{{ getString('funnel_choice_intro') }}</p>
							<div class="header-chips">
								<AppChip
									v-for="chip in chips"
									:key="chip.key"
									:icon="chip.icon"
									:color="chip.color"
									:bg-color="chip.bgColor">
									{{ getString(chip.key) }}
								</AppChip>
							</div>
						</div>

						<div class="cards-row">
							<FunnelChoiceCard
								v-for="card in cards"
								:key="card.id"
								:number="card.number"
								:title="card.title"
								:image="card.image"
								:lead="card.lead"
								:bullets="card.bullets"
								:cta-text="card.cta"
								@choose="chooseCard(card)" />
						</div>

						<div class="choices-footer">
							<div class="footer-heading">
								<p class="footer-title">{{ getString('funnel_choice_footer_title') }}</p>
								<p class="footer-text">{{ getString('funnel_choice_footer_text') }}</p>
							</div>
							<a class="contact-button" href="https://widget.spoki.com/wd/0ea7dcfe-7cb6-44f6-b010-97c9ef7d2bd7/" target="_blank" rel="noopener">
								<div class="contact-icon" v-html="getIcon('iconWhatsapp')"></div>
								<span>{{ getString('funnel_choice_contact') }}</span>
							</a>
						</div>
					</div>
				</template>

				<!-- Page 2: follow-up step for the selected path -->
				<template #next-step>
					<FunnelNextStep
						:card="selectedCard"
						@back="pageIndex = 0"
						@book="openCalendly"
						@finish="finish" />
				</template>

			</AppPagination>
		</div>
	</AppPopup>

</template>

<script>
import {useLanguageStore, useSvgStore, useScriptStore} from "@/shared/stores";
import {useUserStore, useFunnelStore} from "@/console-new/stores";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppPagination from "@/shared/components/AppPagination.vue";
import AppChip from "@/shared/components/AppChip.vue";
import FunnelChoiceCard from "@/console-new/components/shared/FunnelChoiceCard.vue";
import FunnelNextStep from "@/console-new/components/shared/FunnelNextStep.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	name: "FunnelChoicePopup",
	setup() {
		const languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		const userStore = useUserStore();
		const scriptStore = useScriptStore();
		const funnelStore = useFunnelStore();
		return {getString: languageStore.getString, getIcon: svgStore.get, userStore, scriptStore, funnelStore};
	},
	components: {AppPopup, AppPagination, AppChip, FunnelChoiceCard, FunnelNextStep},
	mixins: [WindowWidth],
	emits: ["close"],
	data() {
		return {
			CALENDLY_URL: 'https://calendly.com/demo-qromo/parla-con-uno-dei-nostri-esperti-utente-qromo/?utm_source=onboarding_console&utm_campaign=onboarding_console&utm_content=',
			pageIndex: 0,
			selectedCardId: null,
			pages: [
				{id: 'choices', slotName: 'choices', title: ''},
				{id: 'next-step', slotName: 'next-step', title: ''},
			],
			chips: [
				{key: 'funnel_choice_badge_free', icon: 'iconCircleV', color: 'var(--color-primary)', bgColor: 'var(--color-alpha-1)'},
				{key: 'funnel_choice_badge_fast', icon: 'iconClock', color: 'var(--color-primary)', bgColor: 'var(--color-alpha-1)'},
				{key: 'funnel_choice_badge_nostrings', icon: 'iconLock', color: 'var(--color-primary)', bgColor: 'var(--color-alpha-1)'},
			],
		};
	},
	computed: {
		cards() {
			let testPart = TEST ? 'TEST-' : '';
			return [
				{
					id: 'mobile',
					number: 1,
					calendlyUtm: testPart+'mobilita',
					title: this.getString('funnel_choice_card1_title'),
					image: 'https://img.qromo.io/img/funnel/funnel-new-vendi-in-mobilita.jpg',
					lead: this.getString('funnel_choice_card1_lead'),
					bullets: [
						this.getString('funnel_choice_card1_b1'),
						this.getString('funnel_choice_card1_b2'),
					],
					cta: this.getString('funnel_choice_card1_cta'),
				},
				{
					id: 'counter',
					number: 2,
					calendlyUtm: testPart+'banco',
					title: this.getString('funnel_choice_card2_title'),
					image: 'https://img.qromo.io/img/funnel/funnel-new-vendita-banco.jpg',
					lead: this.getString('funnel_choice_card2_lead'),
					bullets: [
						this.getString('funnel_choice_card2_b1'),
						this.getString('funnel_choice_card2_b2'),
					],
					cta: this.getString('funnel_choice_card2_cta'),
				},
				{
					id: 'complete',
					number: 3,
					calendlyUtm: testPart+'completa',
					title: this.getString('funnel_choice_card3_title'),
					image: 'https://img.qromo.io/img/funnel/funnel-new-gestione-completa.jpg',
					lead: this.getString('funnel_choice_card3_lead'),
					bullets: [
						this.getString('funnel_choice_card3_b1'),
						this.getString('funnel_choice_card3_b2'),
						this.getString('funnel_choice_card3_b3'),
					],
					cta: this.getString('funnel_choice_card3_cta'),
				},
			];
		},
		selectedCard() {
			return this.cards.find(card => card.id === this.selectedCardId) || this.cards[0];
		},
	},
	methods: {
		chooseCard(card) {
			this.selectedCardId = card.id;
			this.pageIndex = 1;
		},
		openCalendly() {
			let url = this.CALENDLY_URL + this.selectedCard.calendlyUtm;
			this.scriptStore.loadScript('calendly', () => {
				if(typeof Calendly !== 'undefined' && Calendly?.initPopupWidget) {
					Calendly.initPopupWidget({url});
				}
			});
		},
		finish() {
			// Persist the chosen card (state + localStorage) so the funnel store
			// resolves the right funnel immediately, without a stale getter cache.
			this.funnelStore.setFunnelChoice(this.selectedCard.id);
			this.$refs.popup.toggleStatus(false);
			this.$emit('close');
		},
	},
	mounted() {
		let popup = this.$refs?.popup;
		if(popup && !popup?.active) {
			popup.toggleStatus(true);
		}
	},
}
</script>

<style lang="scss" scoped>
.funnel-choice-wrapper {
	padding: 20px 0;
	width: 100%;
}

.choices-page {
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 100%;
}

.choices-header {
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
	padding-right: 12px;

	.header-heading {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.header-title {
		font-size: 32px;
		font-weight: 700;
		color: var(--color-primary);
	}

	.header-subtitle {
		font-size: 22px;
		font-weight: 700;
		color: var(--color-txt);
	}

	.header-intro {
		font-size: 16px;
		color: var(--color-txt);
	}

	.header-chips {
		display: flex;
		flex-wrap: wrap;
		gap: 15px;
	}
}

.cards-row {
	display: flex;
	gap: 20px;
	align-items: stretch;
	width: 100%;
	padding-right: 12px;
}

.choices-footer {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 20px;
	width: 100%;
	padding-right: 12px;

	.footer-heading {
		display: flex;
		flex-direction: column;
		gap: 10px;
		color: var(--color-txt);
	}

	.footer-title {
		font-size: 22px;
		font-weight: 700;
	}

	.footer-text {
		font-size: 16px;
	}

	.contact-button {
		@include centered;
		@include noSelect;
		flex-shrink: 0;
		gap: 8px;
		padding: 7px 16px;
		max-width: 200px;
		border-radius: 13px;
		background-color: #25d366;
		cursor: pointer;
		transition: filter .3s $transition;

		&::after {
			display: none;
		}

		&:hover { filter: brightness(1.05); }

		.contact-icon {
			@include centered;
			width: 16px;
			height: 16px;

			:deep(svg) {
				width: 16px;
				height: 16px;
				.fill { fill: #fff; }
				.stroke { stroke: #fff; }
			}
		}

		span {
			font-size: 16px;
			font-weight: 600;
			color: #fff;
		}
	}
}

@media (max-width: 768px) {
	.choices-page {
		gap: 20px;
	}

	.choices-header {
		.header-title { font-size: 26px; }
		.header-subtitle { font-size: 18px; }
		.header-chips {
			gap: 6px;
		}
	}

	.cards-row {
		flex-direction: column;
		align-items: center;

		:deep(.funnel-choice-card) {
			width: 100%;
			max-width: 100%;
		}
	}

	.choices-footer {
		flex-direction: column;
		align-items: stretch;

		.contact-button {
			max-width: unset;
			width: 100%;
		}
	}

}
@media (max-width: 600px) {
	.funnel-choice-wrapper {
		padding: 10px 0;
	}
}
</style>

<style lang="scss">
.popup-container:has(#funnel-choice-wrapper) {
	padding: 0 18px 2px 30px;
	.popup-body {
		margin: 0;
	}
}
@media (max-width: 600px) {
	.popup-container:has(#funnel-choice-wrapper) {
		padding: 6px 0 6px 12px;
	}
}
</style>
